import { MemberStage } from './tabData';
import { STABLE_RULE, meetsFrequency } from './passOrder';
import { SessionlessInput, isSessionless } from './sessionless';
import { weeklyPaceText } from './pace';

/**
 * 회원 위험 신호 판정.
 *
 * ─────────────────────────────────────────────────────────────
 * 요약 목록과 상세 카드가 **같은 기준**을 봐야 한다
 * ─────────────────────────────────────────────────────────────
 * 목록은 "빨간 것만 보면 된다"로 읽힙니다. 목록에서 회색이던 회원을 열었더니
 * 상세가 빨갛다면 원장님은 목록을 못 믿게 되고, 반대라면 급한 회원을 지나칩니다.
 *
 * 그래서 판정 기준(임계값)을 이 파일 한 곳에만 둡니다. 화면 코드에
 * `m.remain <= 3` 같은 숫자를 직접 적지 않습니다 — 두 곳에 적으면 반드시 갈립니다.
 *
 * ⚠️ 여기 있는 임계값은 새로 정한 것이 아닙니다. 상세 카드(MemberRow)의 Cell
 *    alert 조건에 원래 적혀 있던 값을 그대로 옮겨온 것입니다. 바꾸면 상세 카드의
 *    붉은 표시가 함께 바뀝니다.
 */

/**
 * 판정에 필요한 값만.
 * MemberView 를 그대로 넘길 수 있고, 나중에 다른 화면이 같은 판정을 쓸 때도
 * 회원 모델 전체를 끌고 오지 않아도 됩니다.
 */
export type MemberSignalInput = SessionlessInput & {
    /** 잔여 회차. 회차 정보가 온전하지 않으면 null */
    remain: number | null;
    /** 만료까지 남은 일수. 만료일이 없으면 null (음수면 이미 지난 것) */
    expiryDday: number | null;
    /** 마지막 수업이 며칠 전인가. 기록이 아예 없으면 null */
    lastLessonDaysAgo: number | null;
    /**
     * 최근 60일 당일 취소·변경 횟수.
     *
     * 세 상태가 다 다릅니다 — `number`(재봤더니 N회) / `null`(못 쟀다) /
     * **키 없음**(이 필드를 모르는 구버전 서버). 판정은 앞의 둘만 봅니다.
     * 서버가 안 보낸 값으로 경고를 만들면 화면에 없는 근거로 붉은 배지가 뜹니다.
     *
     * ⚠️ 여기에 노쇼율(noshowPct)이 있었습니다. 대표 지시로 회원 현황의
     *    **표시에서 노쇼율을 빼고** 이 값으로 교체했습니다. 서버는 noshowPct 를
     *    그대로 보내므로, 되살리고 싶어지면 그 지시를 먼저 확인하십시오.
     */
    sameDayAdjustments?: number | null;
    /** 소진 페이스 변화율(%). 비교할 이전 기록이 없으면 null */
    paceDeltaPct: number | null;
    /**
     * 최근 60일 수업 수 (서버 `paceNow`). **주당 횟수가 아니라 총합**입니다.
     *
     * 변화율이 못 보는 절대량을 봅니다 — 원래 주 0.3회로 다니던 회원은
     * 변화가 없어서 `paceDeltaPct` 로는 영원히 안 걸립니다.
     * 모르면(undefined) 배지를 만들지 않습니다.
     */
    paceNow?: number;
    /**
     * 소진 페이스 변화율 비교선을 넘겼는가를 판정할 때 함께 봅니다.
     * 값의 뜻과 규칙은 `passOrder.StageInput` 의 같은 이름 필드와 **동일**합니다 —
     * 배지와 단계 판정이 같은 입력을 봐야 두 화면이 같은 말을 합니다.
     */
    centerLessonsPerWeekMedian?: number | null;
    /** 위 중앙값을 낸 회원 수. 표본이 얇으면 믿지 않습니다 */
    centerLessonsSampleSize?: number | null;
};

/**
 * 당일 취소·변경이 몇 회부터 경고인가.
 *
 * ⚠️ **서버 판정과 같은 숫자여야 합니다.** 서버 `opsMemberData.SAME_DAY_ADJUST_ALERT = 3`
 *    이고 브리핑 카드의 근거 수치가 `>= 3` 에서 alert 로 뜹니다
 *    (opsBriefing.scheduler). 여기만 4로 올리면 같은 회원이 오늘의 브리핑에서는
 *    붉고 회원 현황에서는 멀쩡한 화면이 됩니다.
 *
 * 🔴 단계 판정도 이 값을 씁니다 — `passOrder.STABLE_RULE.sameDayAdjustments`
 *    에서 그대로 읽어옵니다. 숫자를 여기 다시 적으면, 붉은 배지가 붙은 회원이
 *    단계는 '안정 궤도'인 화면이 만들어집니다.
 */
export const SAME_DAY_ADJUST_ALERT = STABLE_RULE.sameDayAdjustments;

/**
 * 며칠 비면 위험으로 볼 것인가.
 *
 * 이탈 위험·골든타임 구간에서는 일주일만 비어도 이미 늦습니다.
 * 안정 구간과 같은 기준(2주)을 쓰면 정작 급한 회원이 안전해 보입니다.
 *
 * ⚠️ 안정 구간 값은 `passOrder.STABLE_RULE.maxGapDays` 를 그대로 씁니다.
 *    단계 판정(resolveStage)이 "이만큼 비면 안정 궤도가 아니다"라고 쓰는 바로 그 숫자라,
 *    여기에 다른 값을 적으면 **목록에 공백 경고가 붙은 회원이 단계는 '안정 궤도'** 인
 *    화면이 됩니다. 숫자는 한 곳에만 있습니다.
 *
 * 「주의」도 안정과 같은 기준(2주)입니다. 이 칸에는 공백이 아닌 이유로 온 회원
 * (오는 횟수가 적거나 당일 취소가 잦은 경우)이 섞여 있어서, 위험 구간의 7일을
 * 대면 아직 잘 다니는 회원에게 공백 배지가 붙습니다.
 */
export const gapAlertDays = (stage: MemberStage): number =>
    stage === 'AT_RISK' || stage === 'GOLDEN' ? 7 : STABLE_RULE.maxGapDays;

/**
 * 소진 페이스가 크게 꺾였는가. 모르면(null) 위험이라고 말하지 않는다.
 *
 * 🔴 문턱은 `passOrder.STABLE_RULE.paceDropPct` 하나입니다 —
 *    단계 판정이 "이만큼 꺾이면 안정이 아니다"라고 쓰는 바로 그 값입니다.
 *    (예전에는 여기 -30 이 직접 박혀 있었고 단계 판정은 이 축을 보지도 않아서,
 *     붉은 배지가 붙은 회원이 '안정 궤도'에 앉아 있었습니다)
 */
export const isPaceAlert = (m: MemberSignalInput): boolean =>
    m.paceDeltaPct != null && m.paceDeltaPct <= STABLE_RULE.paceDropPct;

/**
 * 상담을 꺼낼 시점을 지났는가.
 *
 * 회차 개념이 없는 수강권(무제한·대관)은 **잔여로 판정하지 않습니다.**
 * 등록상 999회라 지금도 걸리지 않지만, 명시해 두지 않으면 목록의 붉은
 * 「잔여 999회」 배지가 언제든 돌아옵니다 (sessionless.ts 주석).
 * 그런 회원의 상담 시점은 만료일(isExpiryAlert)이 말합니다.
 */
export const isRemainAlert = (m: MemberSignalInput): boolean =>
    !isSessionless(m) && m.remain != null && m.remain <= 3;

/**
 * 만료가 2주 안으로 들어왔는가.
 * 이미 지난 건(음수)은 여기서 제외합니다 — 상세 카드가 '이미 만료됨'으로 따로 말합니다.
 */
export const isExpiryAlert = (m: MemberSignalInput): boolean =>
    m.expiryDday != null && m.expiryDday >= 0 && m.expiryDday <= 14;

/** 수업 공백이 단계 기준을 넘었는가 */
export const isGapAlert = (m: MemberSignalInput, stage: MemberStage): boolean =>
    m.lastLessonDaysAgo != null && m.lastLessonDaysAgo >= gapAlertDays(stage);

/**
 * 당일 취소·변경이 잦은가.
 *
 * `null`(못 쟀다)과 키 없음(구서버)은 **위험이라고 말하지 않습니다.**
 * 모르는 것은 "괜찮다"도 "위험하다"도 아닙니다.
 */
export const isSameDayAdjustAlert = (m: MemberSignalInput): boolean =>
    m.sameDayAdjustments != null && m.sameDayAdjustments >= SAME_DAY_ADJUST_ALERT;

/**
 * 오는 횟수 자체가 적은가 — **변화율이 아니라 절대량.**
 *
 * 🔴 이 배지가 없으면 「주의」 칸에 이유 없이 서 있는 회원이 생깁니다.
 *    단계 판정(`passOrder.meetsStable`)이 이 축으로 회원을 내려보내는데
 *    화면에 대응 배지가 없으면 원장님이 "왜 여기 있나"를 읽을 수 없고,
 *    그러면 그 명단은 강사에게 전달되지 않습니다.
 *
 * 🔴 그래서 **판정과 같은 함수**(`passOrder.meetsFrequency`)를 부릅니다.
 *    2026-08-27 대표 결정으로 이 축이 고정 문턱(주 0.5회)에서 **센터 상대 기준**으로
 *    바뀌었습니다. 여기에 옛 고정값을 남겨 두면 「주 0.4회」 배지가 붙은 회원의
 *    단계가 '안정 궤도'인 화면이 만들어집니다 — 이 파일이 막으려던 바로 그것입니다.
 *
 * ⚠️ 지금은 센터 기준선(S1)이 서버에서 안 오므로 이 배지가 **아무에게도 안 붙습니다.**
 *    의도된 상태입니다 (`meetsFrequency` 주석). 기준선이 들어오면 같이 살아납니다.
 *
 * ⚠️ `paceNow` 는 **60일 총합**입니다. 주당 환산은 `pace.weeklyPace()` 한 곳에서만
 *    합니다 — 여기에 `/ 8.57` 을 적으면 목록과 단계 판정의 반올림이 갈립니다.
 *    모르면(undefined) 배지를 만들지 않습니다.
 */
export const isRarePaceAlert = (m: MemberSignalInput): boolean =>
    m.paceNow != null && !meetsFrequency(m);

/**
 * 요약 목록에 붙일 신호 배지.
 *
 * ⚠️ 없는 값으로 배지를 만들지 않습니다. `null` 은 "모른다"이지 "괜찮다"가 아니라서,
 *    모르는 항목은 배지도 만들지 않고 그 칸에 '—' 만 남깁니다.
 *
 * 순서는 급한 순입니다 — 목록에서 잘려도 앞의 것부터 남습니다.
 */
export type SignalTag = {
    key: string;
    text: string;
    /** BAD 는 지금 손대야 하는 것, WARN 은 눈여겨볼 것 */
    tone: 'BAD' | 'WARN';
};

export const signalTags = (m: MemberSignalInput, stage: MemberStage): SignalTag[] => {
    const tags: SignalTag[] = [];

    if (isGapAlert(m, stage)) {
        tags.push({ key: 'gap', text: `${m.lastLessonDaysAgo}일 공백`, tone: 'BAD' });
    }
    if (isPaceAlert(m)) {
        tags.push({ key: 'pace', text: `페이스 ▼${Math.abs(m.paceDeltaPct as number)}%`, tone: 'BAD' });
    }
    /*
     * 페이스 배지와 다른 말을 합니다 — 저건 "줄었다", 이건 "원래 적다".
     * 주 0.3회로 꾸준한 회원은 변화율이 0이라 위 배지가 영원히 안 붙습니다.
     */
    if (isRarePaceAlert(m)) {
        tags.push({
            key: 'rare',
            text: `주 ${weeklyPaceText(m.paceNow as number)}회`,
            tone: 'WARN',
        });
    }
    if (isRemainAlert(m)) {
        tags.push({ key: 'remain', text: `잔여 ${m.remain}회`, tone: 'BAD' });
    }
    /*
     * 이미 지난 만료는 '임박'이 아니라 '지남'입니다.
     * 같은 배지로 뭉개면 아직 시간이 있는 회원과 구분이 안 됩니다.
     */
    if (m.expiryDday != null && m.expiryDday < 0) {
        tags.push({ key: 'expired', text: `만료 ${Math.abs(m.expiryDday)}일 지남`, tone: 'BAD' });
    } else if (isExpiryAlert(m)) {
        tags.push({ key: 'expiry', text: `만료 D-${m.expiryDday}`, tone: 'BAD' });
    }
    /*
     * 여기에 `노쇼 N%` 배지가 있었습니다. 대표 지시로 회원 현황의 표시에서
     * 노쇼율을 빼고 당일 취소·변경으로 교체했습니다 —
     * "안 온 것"보다 "당일에 무른 것"이 원장이 손댈 수 있는 신호라는 판단입니다.
     * 노쇼율 자체는 서버가 그대로 보내고 다른 화면이 씁니다. 여기서만 안 그립니다.
     */
    if (isSameDayAdjustAlert(m)) {
        tags.push({
            key: 'sameDay',
            text: `당일 취소·변경 ${m.sameDayAdjustments}회`,
            tone: 'WARN',
        });
    }

    return tags;
};
